import React, { useRef } from 'react';
import { Tag as TagIcon, CalendarDays, Coins, Gem, Sparkles, Circle, Pencil, Trash2, Image as ImageIcon, Camera, Loader2 } from 'lucide-react';
import { formatGrams, formatCarats, formatDate } from '@/lib/format';
import { deriveStones, hasAnyStone } from './utils';

function StoneRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-muted-foreground">
      <span className="flex items-center gap-1"><Icon size={11} /> {label}</span>
      <span className="font-mono tabular-nums text-foreground">{value}</span>
    </div>
  );
}

/**
 * TagJewelleryCard — image-forward, info-restricted card for the tag domain.
 * Deliberately NEVER shows value/price, goldsmith, or order-form/source-order
 * references. Edit/Delete appear only for manual tags the user may modify.
 * Tag Admins may REPLACE the image on any card (manual or System); a System
 * card's new photo syncs back to the main portal.
 */
export function TagJewelleryCard({ item, onEdit, onDelete, onReplaceImage, uploading = false }) {
  const isManual = !!item.is_manual;
  const canModify = !!item.can_modify;
  const canReplaceImage = !!item.can_replace_image;
  const img = item.image || item.fallback_image || '';
  const purity = item.gold_purity_name || (item.gold_purity_percent ? `${Number(item.gold_purity_percent).toFixed(2)}%` : '');
  const received = item.received_at ? formatDate(item.received_at) : '\u2014';
  const s = deriveStones(item);
  const stones = hasAnyStone(s);
  const fileRef = useRef(null);

  const pickFile = () => { if (!uploading && fileRef.current) fileRef.current.click(); };
  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (f) onReplaceImage?.(item, f);
  };

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow duration-150 hover:shadow-md" data-testid="tagws-jewellery-card">
      <div className="absolute left-2 top-2 z-10">
        {isManual ? (
          <span className="inline-flex items-center rounded-full border border-border bg-[hsl(var(--accent))] px-2 py-0.5 text-[11px] font-medium text-foreground" data-testid="tagws-badge-manual">Manual</span>
        ) : (
          <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground" data-testid="tagws-badge-system">System</span>
        )}
      </div>
      {isManual && canModify && (
        <div className="absolute right-2 top-2 z-10 flex gap-1">
          <button onClick={() => onEdit?.(item)} aria-label="Edit tag" title="Edit" data-testid="tagws-card-edit-button" className="grid h-7 w-7 place-items-center rounded-lg border border-border bg-card/90 text-muted-foreground backdrop-blur transition-colors duration-150 hover:text-foreground"><Pencil size={13} /></button>
          <button onClick={() => onDelete?.(item)} aria-label="Delete tag" title="Delete" data-testid="tagws-card-delete-button" className="grid h-7 w-7 place-items-center rounded-lg border border-border bg-card/90 text-muted-foreground backdrop-blur transition-colors duration-150 hover:border-destructive/40 hover:text-destructive"><Trash2 size={13} /></button>
        </div>
      )}
      <div className="relative aspect-square w-full border-b border-border bg-secondary">
        {img ? (
          <img src={img} alt={item.jewellery_no} className="h-full w-full object-contain p-3" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon size={30} /></div>
        )}
        {canReplaceImage && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileChange}
              data-testid="tagws-card-image-input"
            />
            <button
              type="button"
              onClick={pickFile}
              disabled={uploading}
              aria-label={img ? 'Replace image' : 'Add image'}
              title={img ? 'Replace image' : 'Add image'}
              data-testid="tagws-card-replace-image-button"
              className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-lg border border-border bg-card/90 px-2.5 py-1.5 text-[11px] font-medium text-foreground shadow-sm backdrop-blur transition-colors duration-150 hover:bg-card disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
              {uploading ? 'Uploading\u2026' : (img ? 'Replace' : 'Add image')}
            </button>
            {uploading && (
              <div className="absolute inset-0 grid place-items-center bg-background/40 backdrop-blur-[1px]" data-testid="tagws-card-image-uploading" />
            )}
          </>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <div className="font-mono text-sm font-medium tabular-nums text-foreground" data-testid="tagws-jewellery-number">{item.jewellery_no || '\u2014'}</div>
          <div className="line-clamp-1 text-sm text-muted-foreground" title={item.title} data-testid="tagws-jewellery-title">{item.title || '(untitled)'}</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1" data-testid="tagws-jewellery-type"><TagIcon size={11} /> <span className="truncate">{item.jewellery_type || 'No type'}</span></span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> <span className="font-mono tabular-nums" data-testid="tagws-jewellery-received-date">{received}</span></span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] text-foreground" data-testid="tagws-jewellery-gold-purity"><Coins size={11} className="text-muted-foreground" /> {purity || '\u2014'}</span>
          <span className="text-[11px] text-muted-foreground"><span className="font-mono tabular-nums text-foreground" data-testid="tagws-jewellery-gross-weight">{formatGrams(item.gross_weight_grams || 0)}</span> g gross</span>
          <span className="text-[11px] text-muted-foreground"><span className="font-mono tabular-nums text-foreground" data-testid="tagws-jewellery-net-weight">{formatGrams(item.net_weight_grams || 0)}</span> g net</span>
        </div>
        <div className="mt-auto" data-testid="tagws-jewellery-stone-breakdown">
          {stones ? (
            <div className="space-y-0.5 border-t border-border pt-2 text-[11px]">
              {s.dCt > 0 && <StoneRow icon={Gem} label="Diamonds" value={`${formatCarats(s.dCt)} ct${s.dPcs ? ` \u00b7 ${s.dPcs} pcs` : ''}`} />}
              {s.csCt > 0 && <StoneRow icon={Sparkles} label="Colour Stones" value={`${formatCarats(s.csCt)} ct${s.csPcs ? ` \u00b7 ${s.csPcs} pcs` : ''}`} />}
              {s.pCt > 0 && <StoneRow icon={Circle} label="Pearls" value={`${formatCarats(s.pCt)} ct${s.pPcs ? ` \u00b7 ${s.pPcs} pcs` : ''}`} />}
            </div>
          ) : (
            <div className="border-t border-border pt-2 text-[11px] italic text-muted-foreground">No stones</div>
          )}
        </div>
      </div>
    </div>
  );
}
